import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { MultiValue } from 'react-select';

import { Skeleton } from '@revelio/ui';

import { SelectionListControls } from '../../../../../shared/selection-lists/components/SelectionListControls/SelectionListControls';
import {
  type Option,
  SelectionListSelect,
} from '../../../../../shared/selection-lists/components/SelectionListSelect/SelectionListSelect';
import { useSelectionListsForKeys } from '../../../../../shared/selection-lists/hooks/useSelectionListsForKeys';
import { standardGqlSelectionListKeys } from '../../../../../shared/selection-lists/types';
import { shouldShowCurrentPreviousToggle } from '../../config/currentPreviousConfig';
import { shouldSortSelectedFlatFilterOptions } from '../../constants/filter-constants';
import {
  type ListData,
  useSingleFilterData,
} from '../../contexts/FilterProvider';
import { useFilterState } from '../../hooks/useFilters';
import { useTrackFilterReset } from '../../hooks/useTrackFilterReset';
import type { ListFilter, ListSelection } from '../../types/filters';
import { getNumericIdFromNestedId } from '../../utils/get-numeric-id-from-nested-id';
import { ToggleSelect } from '../ToggleSelect';
import layout from './filterFormLayout.module.css';
import { NotToggle } from './NotToggle';
import styles from './selectionListNotToggle.module.css';

function matchSelectionToOption(
  selection: ListSelection,
  options: Option[]
): Option | null {
  const matchById = options.find((o) => o.value === selection.id);
  if (matchById) return matchById;
  const numericId = getNumericIdFromNestedId(String(selection.id));
  if (numericId !== null) {
    const matchByNumeric = options.find(
      (o) => String(numericId) === String(o.value)
    );
    if (matchByNumeric) return matchByNumeric;
  }
  const matchByLabel = options.find((o) => o.label === selection.name);
  return matchByLabel ?? null;
}

interface ListFilterFormProps {
  filter: ListFilter;
  closeMenu: () => void;
  onSave?: (filter: ListFilter) => void;
  isNotEnabled?: boolean;
  onIsNotEnabledChange?: (isNotEnabled: boolean) => void;
  showNotToggle?: boolean;
}

export const ListFilterForm: React.FC<ListFilterFormProps> = ({
  filter,
  closeMenu,
  onSave,
  isNotEnabled: isNotEnabledProp,
  onIsNotEnabledChange,
  showNotToggle = false,
}) => {
  const data = useSingleFilterData(filter.id);
  const { updateFilter } = useFilterState(filter.id);
  const { fetching: isSelectionListsLoading } = useSelectionListsForKeys(
    standardGqlSelectionListKeys
  );
  const location = useLocation();
  const trackFilterReset = useTrackFilterReset();

  const [selectedOptions, setSelectedOptions] = useState<Option[]>(() => {
    return (
      filter.selections?.map((selection) => ({
        value: selection.id,
        label: selection.name,
      })) || []
    );
  });
  const [isCurrent, setIsCurrent] = useState<boolean>(filter.isCurrent ?? true);
  // isNotEnabled: whether the "not" logic is currently enabled (use prop if provided, otherwise filter value, default to false)
  const isNotEnabled = isNotEnabledProp ?? filter.isNot ?? false;

  const selectionList = (data as ListData[])?.[0]?.selectionList;
  const options: Option[] = useMemo(
    () =>
      selectionList?.map((item) => ({
        value: item.id,
        label: item.longName || item.shortName || item.id,
      })) ?? [],
    [selectionList]
  );

  // Intentionally omit selectedOptions — effect syncs filter.selections → display when options load; including it causes infinite loop when AI-generated IDs don't match options.
  // biome-ignore lint/correctness/useExhaustiveDependencies: sync filter.selections → selectedOptions when options load; selectedOptions omitted to avoid infinite loop.
  useEffect(() => {
    if (options.length === 0 || !filter.selections?.length) return;
    const needsNormalize = selectedOptions.some(
      (s) => !options.some((o) => o.value === s.value)
    );
    if (!needsNormalize) return;
    const normalized = filter.selections
      .map((selection) => {
        const option = matchSelectionToOption(selection, options);
        return (
          option ?? ({ value: selection.id, label: selection.name } as Option)
        );
      })
      .filter(
        (opt, idx, arr) => arr.findIndex((o) => o.value === opt.value) === idx
      );
    setSelectedOptions(normalized);
  }, [options, filter.selections]);

  const handleSelectionChange = (newValue: MultiValue<Option>) => {
    setSelectedOptions([...newValue]);
  };

  const handleSubmit = () => {
    const selections: ListSelection[] = selectedOptions.map((option) => ({
      id: option.value,
      name: option.label,
    }));

    const updatedFilter = {
      ...filter,
      selections,
      isCurrent,
      isNot: isNotEnabled,
      isUserSubmitted: true,
    };

    // Use onSave prop if provided (for MULTI filter support), otherwise fall back to updateFilter
    if (onSave) {
      onSave(updatedFilter);
    } else {
      updateFilter(updatedFilter);
    }
    closeMenu();
  };

  const handleClear = () => {
    trackFilterReset(filter, { selectionCountBefore: selectedOptions.length });
    setSelectedOptions([]);
  };

  const showToggle = shouldShowCurrentPreviousToggle(
    filter.id,
    location.pathname
  );

  if (isSelectionListsLoading && options.length === 0) {
    return (
      <div className={layout.skeletonStack}>
        <Skeleton className={layout.skeletonLine} />
        <Skeleton className={layout.skeletonLine} />
        <Skeleton className={layout.skeletonLine} />
      </div>
    );
  }

  return (
    <div className={layout.formStack}>
      <div
        className={classNames(
          layout.fieldStack,
          layout.notToggleAnchor,
          showNotToggle && styles.notToggleLayout
        )}
      >
        {showNotToggle && onIsNotEnabledChange && (
          <div className={styles.notToggleRow}>
            <NotToggle
              filterName={filter.name}
              isNot={isNotEnabled}
              onChange={onIsNotEnabledChange}
            />
          </div>
        )}
        <SelectionListSelect
          options={options}
          value={selectedOptions}
          maxMenuHeight={showNotToggle ? 180 : 220}
          onChange={handleSelectionChange}
          isMulti
          sortSelectedOptions={shouldSortSelectedFlatFilterOptions(filter.id)}
        />
        {showToggle && (
          <ToggleSelect
            filterName={filter.name}
            onChange={(e: number) => setIsCurrent(e === 0)}
            initialIsCurrent={isCurrent}
          />
        )}
      </div>
      <SelectionListControls
        onClear={handleClear}
        onSubmit={handleSubmit}
        onClose={closeMenu}
        submitText={filter.selections?.length > 0 ? 'Update' : 'Add'}
        isSubmitDisabled={selectedOptions.length === 0}
      />
    </div>
  );
};
